import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import type {
  FeatureGeometry,
  PlaceCandidate,
  SpatialLayerObject,
} from './GlobalMapSearch.types';

/**
 * Resolve a single (lat, lng) centroid for a spatial object so the map can
 * pan to it / measure distance from the user.
 *
 * Resolution order, mirroring how the rest of the app (StaticLayerCard,
 * EllingtonCard helpers, GeoTiffMapCard) reads coordinates from a spatial
 * object:
 *   1. `spatialObject.center.coordinates` — canonical [lng, lat] centroid
 *   2. `spatialObject.shape.features[0].geometry.coordinates` — Point shape
 *   3. First vertex of a Polygon shape, when present
 */
export const getSpatialObjectCenter = (
  obj: SpatialLayerObject | undefined,
): { latitude: number; longitude: number } | null => {
  if (!obj) return null;

  const centerCoords = obj.center?.coordinates;
  if (
    Array.isArray(centerCoords) &&
    typeof centerCoords[0] === 'number' &&
    typeof centerCoords[1] === 'number'
  ) {
    return { latitude: centerCoords[1], longitude: centerCoords[0] };
  }

  const geom = obj.shape?.features?.[0]?.geometry;
  const raw = geom?.coordinates;

  if (
    Array.isArray(raw) &&
    typeof raw[0] === 'number' &&
    typeof raw[1] === 'number'
  ) {
    return { latitude: raw[1] as number, longitude: raw[0] as number };
  }

  if (Array.isArray(raw) && Array.isArray(raw[0])) {
    const ring = raw[0] as unknown as number[][];
    const first = ring?.[0];
    if (
      Array.isArray(first) &&
      typeof first[0] === 'number' &&
      typeof first[1] === 'number'
    ) {
      return { latitude: first[1], longitude: first[0] };
    }
  }

  return null;
};

// Minimum query length for the global map search to fire. The search
// is only ever kicked off by an explicit user action (Enter, the
// trailing "Search" button, or re-running a recent) — it does not
// auto-fire on keystroke — so a single character is enough; we let
// the user search as soon as they've typed *something*.
export const GIS_SEARCH_MIN_LENGTH = 1;
export const HYDRANT_PROXIMITY_KEYWORDS = ['hydrant', 'fire hydrant'];
export const HYDRANT_BUFFER_METERS = 500;
export const IGNORED_SPECIAL_CHARACTERS = /[-+()\s]/g;

const toStr = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};

/** Case-insensitive attribute lookup — ArcGIS field casing varies across services. */
const getAttr = (attrs: Record<string, unknown>, name: string): string => {
  const lower = name.toLowerCase();
  for (const k of Object.keys(attrs)) {
    if (k.toLowerCase() === lower) return toStr(attrs[k]);
  }
  return '';
};

/** Try multiple field name variants and return the first non-empty match. */
const getFirstAttr = (
  attrs: Record<string, unknown>,
  names: string[],
): string => {
  for (const name of names) {
    const val = getAttr(attrs, name);
    if (val) return val;
  }
  return '';
};

const GENERIC_NAME_FIELDS = [
  'facility',
  'name',
  'label',
  'title',
  'description',
  'fullname',
  'full_name',
  'displayname',
  'display_name',
  'sitename',
  'site_name',
  'placename',
  'place_name',
  'location',
  'address',
];

/**
 * Find the best human-readable name from a feature's attributes by checking
 * well-known field names first, then falling back to any field whose key
 * contains a name-like substring.
 */
const findGenericName = (attrs: Record<string, unknown>): string => {
  const explicit = getFirstAttr(attrs, GENERIC_NAME_FIELDS);
  if (explicit) return explicit;

  const namePatterns = ['name', 'label', 'title', 'facility', 'desc'];
  for (const key of Object.keys(attrs)) {
    if (key.startsWith('_')) continue;
    const lower = key.toLowerCase();
    if (namePatterns.some(p => lower.includes(p))) {
      const val = toStr(attrs[key]);
      if (val) return val;
    }
  }
  return '';
};

const PARCEL_OWNER_FIELDS = [
  'Owner',
  'ownname',
  'owner_name',
  'ownername',
  'property_owner',
];
const PARCEL_OWNER2_FIELDS = ['Owner2', 'ownname2', 'coowner', 'owner_name2'];
const PARCEL_ID_FIELDS = [
  'parno',
  'Name',
  'APN',
  'parcel_id',
  'parcelid',
  'parcel_no',
  'PIN',
  'PID',
];

export const getSublayerKind = (title: string | undefined): string | null => {
  const lower = (title || '').toLowerCase();
  if (lower.includes('hydrant')) return 'hydrant';
  if (lower.includes('parcel')) return 'parcel';
  return null;
};

export const isProximitySearch = (text: string): boolean => {
  const lower = (text || '').toLowerCase().trim();
  return HYDRANT_PROXIMITY_KEYWORDS.some(kw => lower.includes(kw));
};

export const haversineDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const formatDistance = (meters: number): string => {
  const ft = meters * 3.28084;
  if (ft < 500) return `${Math.round(ft)} ft`;
  const mi = ft / 5280;
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
};

/**
 * If coordinates are in web mercator (abs > 180 / 90), project them to WGS84.
 * Otherwise return as-is.
 */
export const normalizePlaceCoords = (
  candidate: PlaceCandidate,
): PlaceCandidate => {
  if (!candidate.location) return candidate;
  const { x, y } = candidate.location;
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return candidate;
  const [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
  return { ...candidate, location: { x: lng, y: lat } };
};

export const getFeatureLabel = (
  attrs: Record<string, unknown>,
  kind: string,
): string => {
  if (kind === 'hydrant') {
    const street = getAttr(attrs, 'StreetName');
    return street ? `Hydrant — ${street}` : 'Fire Hydrant';
  }
  if (kind === 'parcel') {
    return (
      getFirstAttr(attrs, PARCEL_OWNER_FIELDS) ||
      getFirstAttr(attrs, PARCEL_ID_FIELDS) ||
      'Parcel'
    );
  }
  return findGenericName(attrs) || 'GIS Feature';
};

const formatFieldName = (raw: string): string =>
  raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

export const getFeatureSublabel = (
  attrs: Record<string, unknown>,
  kind: string,
  geometry: FeatureGeometry | undefined,
  mapCenter: { latitude?: number; longitude?: number },
): string => {
  const parts: string[] = [];

  const matchedField = toStr(attrs._matchedField);
  const matchedValue = toStr(attrs._matchedValue);
  if (matchedField && matchedValue) {
    parts.push(`${formatFieldName(matchedField)}: ${matchedValue}`);
  }

  if (kind === 'hydrant') {
    const status = (attrs.Status as string) || (attrs.STATUS as string) || '';
    if (status) parts.push(`Status: ${status}`);

    const lat = geometry?.centroid?.latitude;
    const lng = geometry?.centroid?.longitude;
    if (lat != null && lng != null) {
      parts.push(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      if (mapCenter?.latitude != null && mapCenter?.longitude != null) {
        parts.push(
          formatDistance(
            haversineDistance(
              mapCenter.latitude,
              mapCenter.longitude,
              lat,
              lng,
            ),
          ),
        );
      }
    }
    return parts.join('  ·  ');
  }
  if (kind === 'parcel') {
    const owner = getFirstAttr(attrs, PARCEL_OWNER_FIELDS);
    const owner2 = getFirstAttr(attrs, PARCEL_OWNER2_FIELDS);
    const parcelId = getFirstAttr(attrs, PARCEL_ID_FIELDS);
    if (owner && !parts.some(p => p.includes(owner)))
      parts.push(`Owner: ${owner}`);
    if (owner2) parts.push(`Owner 2: ${owner2}`);
    if (parcelId && !parts.some(p => p.includes(parcelId)))
      parts.push(`Parcel #: ${parcelId}`);
    return parts.join('  ·  ');
  }

  const sourceLayer = toStr(attrs._sourceLayer);
  if (sourceLayer) parts.push(sourceLayer);
  const addr =
    getAttr(attrs, 'address') ||
    getAttr(attrs, 'location') ||
    getAttr(attrs, 'addr');
  if (addr && !parts.some(p => p.includes(addr))) parts.push(addr);
  if (
    geometry?.centroid &&
    mapCenter?.latitude != null &&
    mapCenter?.longitude != null
  ) {
    parts.push(
      formatDistance(
        haversineDistance(
          mapCenter.latitude,
          mapCenter.longitude,
          geometry.centroid.latitude,
          geometry.centroid.longitude,
        ),
      ),
    );
  }
  return parts.join('  ·  ');
};
